import { isSystemAudioZone } from './localZoneStorage';

export type SignalPathZoneType = 'system_audio' | 'native' | 'network';

export type ZoneLike = { type?: string; id?: string; name?: string } | null | undefined;

const LOCAL_OUTPUT_TYPES = new Set(['local', 'browser', 'local-native']);

/** Classify active output for signal-path badge and telemetry (v1: three kinds). */
export function classifySignalPathZone(zone: ZoneLike): SignalPathZoneType | null {
  if (!zone) return null;
  if (zone.type === 'local-native') return 'native';
  if (isSystemAudioZone(zone)) return 'system_audio';
  if (LOCAL_OUTPUT_TYPES.has(String(zone.type || ''))) return 'system_audio';
  return 'network';
}

export function getSignalPathBadge(zone: ZoneLike): {
  labelKey: string;
  tooltipKey: string;
} | null {
  const zoneType = classifySignalPathZone(zone);
  if (zoneType === 'system_audio') {
    return {
      labelKey: 'signal_path.badge_shared',
      tooltipKey: 'signal_path.badge_shared_tooltip',
    };
  }
  if (zoneType === 'network') {
    return {
      labelKey: 'signal_path.badge_direct',
      tooltipKey: 'signal_path.badge_direct_tooltip',
    };
  }
  return null;
}

/** True when the zone list includes at least one local-native (USB DAC) output. */
export function zoneListHasNativeLocalDevice(
  zones: Array<{ type?: string }> | null | undefined,
): boolean {
  if (!Array.isArray(zones)) return false;
  return zones.some((z) => z?.type === 'local-native');
}

export const SIGNAL_PATH_NUDGE_PLAYBACK_MS = 10 * 60 * 1000;
export const SIGNAL_PATH_NUDGE_SUPPRESS_MS = 30 * 24 * 60 * 60 * 1000;

/** Pure gate for the one-time System Audio → native output nudge (v1). */
export function shouldShowSignalPathNudge(args: {
  zoneType: SignalPathZoneType | null;
  hasNativeDevice: boolean;
  playbackMs: number;
  suppressed: boolean;
  isTransitioning: boolean;
  msSinceTrackChange: number;
}): boolean {
  if (args.suppressed) return false;
  if (args.zoneType !== 'system_audio' || !args.hasNativeDevice) return false;
  if (args.playbackMs < SIGNAL_PATH_NUDGE_PLAYBACK_MS) return false;
  if (args.isTransitioning) return false;
  if (args.msSinceTrackChange < 8000) return false;
  return true;
}
